import { url } from '@exodus/fetch/url';
import { byArguments as makeConcurrentByArgs } from 'make-concurrent';
import { ALLOWED_SOURCES, DEFAULT_BASE_URL } from './constants.js';
import { createUnitType } from './utils.js';
import { memoize } from '@exodus/basic-utils';
import { AssetsServiceError } from '../../errors.js';
const MEMOIZATION_TIMEOUT = 1000 * 60 * 5;
class AssetsService {
    _baseUrl;
    _fetchival;
    _cachedAssets = new Map();
    _cachedAssetsByNetwork = new Map();
    constructor({ baseUrl = DEFAULT_BASE_URL, fetchival }) {
        this._baseUrl = new URL(baseUrl);
        this._fetchival = fetchival;
    }
    getOneById = makeConcurrentByArgs(async (id) => {
        if (this._cachedAssets.has(id)) {
            return this._cachedAssets.get(id) || null;
        }
        const networkWithAsset = [...this._cachedAssetsByNetwork.values()].find((networkAssets) => networkAssets.has(id));
        if (networkWithAsset) {
            return networkWithAsset.get(id) || null;
        }
        const asset = await this._fetchival(url `${this._baseUrl}/assets/${id}`).get();
        asset.currency = createUnitType(asset.id, asset.decimals);
        this._cachedAssets.set(id, asset || null);
        return asset;
    });
    async getAllByNetworks(networks, opts) {
        const uncachedNetworks = networks.filter((network) => !this._cachedAssetsByNetwork.has(network));
        if (uncachedNetworks.length > 0) {
            await this._loadNetworks(uncachedNetworks, opts);
        }
        return new Map([...this._cachedAssetsByNetwork.entries()].filter(([network]) => networks.includes(network)));
    }
    getAssetIdsBySource = async (source) => {
        if (!ALLOWED_SOURCES.includes(source)) {
            throw new AssetsServiceError({
                message: `Invalid source (${source}) for getAssetIdsBySource`,
            });
        }
        try {
            const response = await this._fetchival(url `${this._baseUrl}/assets`, {
                responseAs: 'text',
            }).get({
                source,
                format: 'csv',
            });
            if (typeof response !== 'string') {
                throw new TypeError('Unexpected response type from Exodus API (getAssetsBySource)');
            }
            return response.split(',');
        }
        catch (err) {
            console.log(err);
        }
        return [];
    };
    getAssetSources = memoize(async (sources, ids) => {
        sources.forEach((source) => {
            if (!ALLOWED_SOURCES.includes(source)) {
                throw new Error(`Invalid source (${source})`);
            }
        });
        try {
            return await this._fetchival(url `${this._baseUrl}/asset-sources`).get({
                sources: sources.join(','),
                assetIds: ids.join(','),
                groupBy: 'sources',
            });
        }
        catch (err) {
            console.log(err);
        }
        return [];
    }, (sources, ids) => sources.join(',') + ids.join(','), MEMOIZATION_TIMEOUT);
    async _loadNetworks(networks, opts) {
        const { limit } = opts || {};
        const assets = await this._fetchival(url `${this._baseUrl}/assets`).get({
            networks: networks.join(','),
            limit: limit || networks.length * 10_000,
        });
        const assetsWithCurrency = assets.map((asset) => ({
            ...asset,
            currency: createUnitType(asset.id, asset.decimals),
        }));
        assetsWithCurrency.forEach((asset) => {
            this._cachedAssets.set(asset.id, asset);
        });
        const assetsByNetworks = assetsWithCurrency.reduce((networkGroups, asset) => {
            const assetsInNetwork = networkGroups.get(asset.network) || [];
            assetsInNetwork.push(asset);
            return networkGroups.set(asset.network, assetsInNetwork);
        }, new Map());
        assetsByNetworks.forEach((assetsInNetwork, network) => {
            if (assetsInNetwork.length > 0) {
                const indexedAssets = new Map(assetsInNetwork.map((asset) => [asset.id, asset]));
                this._cachedAssetsByNetwork.set(network, indexedAssets);
            }
        });
    }
}
export default AssetsService;
